import { useSelector } from '@xstate/vue'
import consola from 'consola'
import { createActor } from 'xstate'
import { appMachine } from '~/machines/app.machine'
import { useNuxtApp, useRuntimeConfig } from '#imports'

const logger = consola.withTag('[App Machine]')

const STORAGE_KEY = 'app-store'

// Module-level singleton — safe because ssr: false (SPA mode)
let actorInstance: ReturnType<typeof createActor<typeof appMachine>> | null = null

function getDefaultLocale(): string {
  const navLang = navigator.language.split('-')[0].toLowerCase()
  if (navLang) {
    return navLang
  }
  return useRuntimeConfig().public.tolgee.defaultLanguage
}

function getPersistedState(): { locale?: string; locales?: string[] } {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    return stored ? JSON.parse(stored) : {}
  } catch {
    return {}
  }
}

function getOrCreateActor() {
  if (actorInstance) return actorInstance

  const runtimeConfig = useRuntimeConfig()
  const persistedState = getPersistedState()

  const initialLocale = persistedState.locale || getDefaultLocale()
  const locales = persistedState.locales || runtimeConfig.public.tolgee.languages

  const configuredMachine = appMachine.provide({
    actions: {
      persistState: ({ context }) => {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ locale: context.locale, locales: context.locales }))
      },
      changeLanguage: (_: unknown, params: { lang: string }) => {
        const { $tolgee } = useNuxtApp()
        $tolgee.run().then(() => {
          $tolgee.changeLanguage(params.lang)
        })
      },
    },
  })

  actorInstance = createActor(configuredMachine, {
    input: { initialLocale, locales },
  })

  actorInstance.start()
  logger.debug('App machine actor created and started')

  // Persist initial state
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ locale: initialLocale, locales }))

  return actorInstance
}

/**
 * Main composable for app state machine.
 * Returns the actor, send function, and reactive selectors.
 */
export function useAppMachine() {
  const actor = getOrCreateActor()

  // Reactive selectors via @xstate/vue
  const locale = useSelector(actor, (s) => s.context.locale)
  const locales = useSelector(actor, (s) => s.context.locales)

  const setLanguage = (lang: string) => {
    actor.send({ type: 'SET_LANGUAGE', lang })
  }

  return {
    actor,
    locale,
    locales,
    setLanguage,
  }
}
